import { isDefined } from '@priva/utilities/predicate';

import {
    multipleContainersListDefinition,
    multipleLocationsListDefinition,
    singleBatchListDefinition,
    singleContainerListDefinition,
    singleEmployeeListDefinition,
    singleLocationsListDefinition,
} from 'app/features/scan-flow/models/scanned-code-list-definition';
import { ScanFlowRegistrationData } from 'app/features/scan-flow/state/scan-flow/scan-flow.reducer';
import {
    isBatchRequired,
    isLocationTypeNullOrSite,
    isOneOrManyContainersRequired,
    isOneOrManyLocationRequired,
    isOneOrManyProductBoxRegistrationsRequired,
    isProductBoxListRequired,
    isSingleAmountResultRequired,
    isSingleContainerRequired,
    isSingleLocationRequired,
} from 'app/features/scan-flow/utils';
import { AppMode } from 'app/features/settings/models';
import { Activity } from 'app/shared/features/activity-selector/models';
import { resetProductBoxRegistrationAmounts } from 'app/shared/utils';

import {
    ScanCodeListDefinition,
    ScannedCode,
    scannedCodeTypes,
    ScannedCodeWithEntity,
    singleActivityListDefinition,
} from '../../models';
import {
    hasBatchPropertyOnly,
    isActivityWithProductBoxRequirement,
    isEmployeeRequired,
    isPackingActivity,
} from '../../utils/scan-activity.utils';
import { filterByCodeType, getFirstByCodeType } from '../../utils/scanned-code-list.utils';
import { isResultRegistrationActivity } from './scan-flow-result-registration/scan-flow-result-registration.utils';

/** Gets the scan flow code definitions based on the activity requirements.
 * @returns An array of ScanCodeListDefinition objects representing the required scan code definitions for the activity.
 */
export function getScanFlowCodeDefinitionsForActivity(
    activity: Activity | undefined,
    appMode: AppMode,
): ScanCodeListDefinition[] {
    let codeDefinitions = [singleActivityListDefinition];

    if (!activity) return codeDefinitions;

    if (isEmployeeRequired(appMode)) codeDefinitions = [...codeDefinitions, singleEmployeeListDefinition];

    if (isBatchRequired(activity)) {
        codeDefinitions = [...codeDefinitions, singleBatchListDefinition];
    }

    if (!isLocationTypeNullOrSite(activity)) {
        if (isSingleLocationRequired(activity)) {
            codeDefinitions = [...codeDefinitions, singleLocationsListDefinition];
        } else if (isOneOrManyLocationRequired(activity)) {
            codeDefinitions = [...codeDefinitions, multipleLocationsListDefinition];
        }
    }

    if (isSingleContainerRequired(activity) || isPackingActivity(activity)) {
        codeDefinitions = [...codeDefinitions, singleContainerListDefinition];
    } else if (isOneOrManyContainersRequired(activity)) {
        codeDefinitions = [...codeDefinitions, multipleContainersListDefinition];
    }

    return codeDefinitions;
}

/** Gets the allowed code types that can still be scanned based current code list definitions and already scanned codes.
 * @param scanFlowCodeListDefinitions The scan flow code list definitions.
 * @param scannedCodes The already scanned codes.
 * @returns An array of allowed code types that can still be scanned.
 */
export function getAllowedCodeTypes({
    scanFlowCodeListDefinitions,
    scannedCodes,
}: {
    scanFlowCodeListDefinitions: ScanCodeListDefinition[];
    scannedCodes: ScannedCode[];
}) {
    return scannedCodeTypes.filter((codeType) => {
        if (codeType === 'activity') return true;

        const listDefinition = getFirstByCodeType(scanFlowCodeListDefinitions, codeType);

        if (!listDefinition) return false;

        if (!listDefinition.maxLength) return true;

        // For batch, we allow multiple batches to be scanned
        if (codeType === 'batch') return true;

        const currentCodes = filterByCodeType(scannedCodes, codeType);

        return currentCodes.length < listDefinition.maxLength;
    });
}

function isMultiStepScanFlow(activity: Activity, appMode: AppMode) {
    return (
        isEmployeeRequired(appMode) ||
        isSingleLocationRequired(activity) ||
        isOneOrManyLocationRequired(activity) ||
        isOneOrManyContainersRequired(activity) ||
        isSingleContainerRequired(activity) ||
        isSingleAmountResultRequired(activity) ||
        isOneOrManyProductBoxRegistrationsRequired(activity) ||
        isResultRegistrationActivity(activity) ||
        isBatchRequired(activity)
    );
}

export function getNextActivityAfterRegistration({
    currentActivity,
    appMode = 'personal',
}: {
    currentActivity: Activity | undefined;
    appMode?: AppMode;
}) {
    if (!currentActivity) return undefined;

    if (isMultiStepScanFlow(currentActivity, appMode)) {
        return currentActivity;
    }

    return undefined;
}

/** Gets the updated scanned codes based on the new activity code and existing batch code.
 *  If activity contains only batch property and requires to keep registration data, the new activity code is returned.
 *  @param newActivityCode The newly scanned activity code with its associated entity.
 *  @param batchScannedCode The currently scanned batch code, if any.
 *  @param keepRegistrationData A flag indicating whether to keep existing registration data.
 *  @returns An array of updated scanned codes.
 */
const getUpdatedScannedCodes = (
    newActivityCode: ScannedCodeWithEntity<Activity>,
    batchScannedCode: ScannedCode | undefined,
    keepRegistrationData = false,
) =>
    (hasBatchPropertyOnly(newActivityCode.entity) && !keepRegistrationData) ||
    !isBatchRequired(newActivityCode.entity)
        ? [newActivityCode]
        : [newActivityCode, batchScannedCode].filter((newScannedCode) => isDefined(newScannedCode));

/**
 * Sets the scan flow state when a new activity number with a similar activity number is scanned/changed.
 * @param ScanFlowRegistrationData The current scan flow registration data.
 * @param newActivityCode The newly scanned activity code with its associated entity.
 * @param isContainerMeasurement A flag indicating if the activity is a container measurement.
 * @returns An object containing the updated scan flow registration data.
 */
export const setScanFlowStateWithSimilarActivityNumber = (
    { scannedCodes, productBoxRegistrations }: ScanFlowRegistrationData,
    newActivityCode: ScannedCodeWithEntity<Activity>,
    isContainerMeasurement?: boolean,
): Partial<ScanFlowRegistrationData> => {
    const prefilledProductBoxRegistrations = resetProductBoxRegistrationAmounts(
        productBoxRegistrations,
        !isProductBoxListRequired(newActivityCode.entity),
    );
    const batchScannedCode = getFirstByCodeType(scannedCodes, 'batch');

    let updatedScannedCodes = [];
    if (isContainerMeasurement === true) {
        // Result registration activity
        // Keep activity, employee, location and batch codes
        const locationScannedCodes = getFirstByCodeType(scannedCodes, 'location');
        const employeeScannedCode = getFirstByCodeType(scannedCodes, 'employee');
        updatedScannedCodes = [
            newActivityCode,
            locationScannedCodes,
            batchScannedCode,
            employeeScannedCode,
        ].filter((newScannedCode) => isDefined(newScannedCode));

        return {
            amountResult: undefined,
            productBoxRegistrations: prefilledProductBoxRegistrations,
            scannedCodes: updatedScannedCodes,
        };
    }
    /** Labor registration activity
     * Keep activity and batch codes
     * Note: batch code is kept only if it has location or result determination type
     */
    updatedScannedCodes = getUpdatedScannedCodes(newActivityCode, batchScannedCode);

    return {
        amountResult: undefined,
        productBoxRegistrations: prefilledProductBoxRegistrations,
        rowSelection: [],
        scannedCodes: updatedScannedCodes,
    };
};

/**
 * Sets the scan flow state when a new activity number is scanned.
 *
 * @param ScanFlowRegistrationData The current scan flow registration data.
 * @param newActivityCode The newly scanned activity code with its associated entity.
 * @param batchCode The currently scanned batch code, if any.
 * @param keepRegistrationData A flag indicating whether to keep existing registration data.
 * @returns An object containing the updated scan flow registration data.
 */

export const setScanFlowStateWithNewActivityNumber = (
    { productBoxRegistrations, batchNumber }: ScanFlowRegistrationData,
    newActivityCode: ScannedCodeWithEntity<Activity>,
    batchCode: ScannedCode | undefined,
    keepRegistrationData = false,
): Partial<ScanFlowRegistrationData> => {
    const newActivity = newActivityCode.entity;
    // If local storage have expired, there's no "old activity" to compare with
    const updatedScannedCodes = getUpdatedScannedCodes(newActivityCode, batchCode, keepRegistrationData);
    const currentBatchNumber =
        keepRegistrationData && isBatchRequired(newActivity) ? (batchCode?.number ?? batchNumber) : undefined;

    // Reset product box registrations if needed
    const newProductBoxRegistrations =
        isActivityWithProductBoxRequirement(newActivity) && !keepRegistrationData
            ? resetProductBoxRegistrationAmounts(
                  productBoxRegistrations,
                  !isProductBoxListRequired(newActivity),
              )
            : [];

    return {
        amountResult: undefined,
        batchNumber: currentBatchNumber,
        productBoxRegistrations: newProductBoxRegistrations,
        rowSelection: [],
        scannedCodes: updatedScannedCodes,
    };
};
